import { requestTaskPage, requestTaskPrefix } from '../platform/taskList';
import type {TaskFilters} from '../platform/taskList';
import { reconcileList } from '../platform/reconcileList';
import { create } from 'zustand';
import type { ClearResultsResult, DeleteOutputsResult, Task } from '../types';
import { apiRequest } from '../platform/api';
import { captureSessionEpoch, isSessionEpochCurrent } from '../platform/sessionEpoch';

interface ResultState {
  filters:TaskFilters;
  loadError:string|null;
  setFilters:(filters:TaskFilters)=>Promise<void>;
  results: Task[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  loadMoreResults: () => Promise<void>;
  loadResults: (maxAgeMs?: number) => Promise<void>;

  deleteResult: (id: string) => Promise<void>;
  deleteOutputs: (outputIds: string[]) => Promise<DeleteOutputsResult>;
  clearAllResults: () => Promise<ClearResultsResult>;
  exportResult: (taskId: string, outputIds: string[], exportDir: string) => Promise<string[]>;
  reset: () => void;
}

let resultLoadSequence = 0;
let resultEpoch = 0;

let resultsCursor: string | null = null;
let resultsLoadedCount = 6;
let activeResultMore: { epoch: ReturnType<typeof captureResultEpoch>; promise: Promise<void> } | undefined;
let lastResultLoad: { at: number; epoch: ReturnType<typeof captureResultEpoch> } | undefined;
let activeResultLoad: { epoch: ReturnType<typeof captureResultEpoch>; promise: Promise<void> } | undefined;

function captureResultEpoch() {
  return { store: resultEpoch, session: captureSessionEpoch() };
}

function isResultEpochCurrent(epoch: ReturnType<typeof captureResultEpoch>): boolean {
  return epoch.store === resultEpoch && isSessionEpochCurrent(epoch.session);
}

export const useResultStore = create<ResultState>((set,get) => ({
  filters:{},loadError:null,
  setFilters:async filters=>{get().reset();set({filters:{...filters}});await get().loadResults();},
  results: [],
  isLoading: false,
  isLoadingMore: false,
  hasMore: false,

  loadResults: async (maxAgeMs = 0) => {
    // Page mounts may reuse an already fresh account load. Event/mutation reads
    // use the default zero age and always fetch after their invalidation.
    if (maxAgeMs > 0 && lastResultLoad && isResultEpochCurrent(lastResultLoad.epoch)
      && Date.now() - lastResultLoad.at < maxAgeMs) return;
    if (maxAgeMs > 0 && activeResultLoad && isResultEpochCurrent(activeResultLoad.epoch)) {
      await activeResultLoad.promise;
      return;
    }
    const epoch = captureResultEpoch();
    if (activeResultMore && isResultEpochCurrent(activeResultMore.epoch)) await activeResultMore.promise.catch(() => undefined);
    if (!isResultEpochCurrent(epoch)) return;
    const requestSequence = ++resultLoadSequence;
    const promise = (async () => {
      set({ isLoading: true,loadError:null });
      try {
        const page = window.CLOUD_RUNTIME ? await requestTaskPrefix('results', resultsLoadedCount, 100,get().filters) : null;
        const results = page ? page.items : await window.electron.invoke(window.IPC_CHANNELS.RESULT_LIST);
        if (requestSequence !== resultLoadSequence || !isResultEpochCurrent(epoch)) return;
        resultsCursor = page?.nextCursor || null;
        set({ hasMore: Boolean(resultsCursor) });
        lastResultLoad = { at: Date.now(), epoch };
        set(state => ({ results: reconcileList(state.results, results), isLoading: false }));
      } catch (error) {
        console.error('Failed to load results:', error);
        if (requestSequence === resultLoadSequence && isResultEpochCurrent(epoch)) set({ isLoading: false,loadError:error instanceof Error?error.message:'结果加载失败，请重试' });
      }
    })();
    const entry = { epoch, promise };
    activeResultLoad = entry;
    try { await promise; }
    finally { if (activeResultLoad === entry) activeResultLoad = undefined; }
  },

  loadMoreResults: async () => {
    if (!window.CLOUD_RUNTIME) return;
    if (activeResultMore && isResultEpochCurrent(activeResultMore.epoch)) return activeResultMore.promise;
    const epoch = captureResultEpoch();
    // A newer forced refresh can replace the request while we are waiting.
    // Keep waiting for the current refresh before choosing its cursor.
    while (activeResultLoad && isResultEpochCurrent(activeResultLoad.epoch)) {
      const pending = activeResultLoad;
      await pending.promise;
      if (!isResultEpochCurrent(epoch)) return;
      if (activeResultLoad === pending) break;
    }
    if (activeResultMore && isResultEpochCurrent(activeResultMore.epoch)) return activeResultMore.promise;
    if (!isResultEpochCurrent(epoch) || !resultsCursor) return;
    const sequence = resultLoadSequence;
    const cursor = resultsCursor;
    set({ isLoadingMore: true });
    const promise = (async () => {
      try {
        const page = await requestTaskPage('results', 6, cursor,get().filters);
        if (sequence !== resultLoadSequence || !isResultEpochCurrent(epoch)) return;
        resultsCursor = page.nextCursor;
        resultsLoadedCount += page.items.length;
        set(state => {
          const ids = new Set(state.results.map(item => item.id));
          return { results: [...state.results, ...page.items.filter(item => !ids.has(item.id))], hasMore: Boolean(resultsCursor) };
        });
      } finally { if (isResultEpochCurrent(epoch)) set({ isLoadingMore: false }); }
    })();
    const entry = { epoch, promise };
    activeResultMore = entry;
    try { await promise; }
    finally { if (activeResultMore === entry) activeResultMore = undefined; }
  },
  deleteResult: async (id) => {
    const epoch = captureResultEpoch();
    await window.electron.invoke(window.IPC_CHANNELS.RESULT_DELETE, { id });
    if (!isResultEpochCurrent(epoch)) return;
    set((state) => ({
      results: state.results.filter((r) => r.id !== id),
    }));
  },

  deleteOutputs: async (outputIds) => {
    const epoch = captureResultEpoch();
    const result = await window.electron.invoke(window.IPC_CHANNELS.RESULT_DELETE_OUTPUTS, { outputIds });
    if (!isResultEpochCurrent(epoch)) return normalizeDeleteResult(result);
    const idSet = new Set(outputIds);
    const removedTaskIds = new Set([
      ...(result.hiddenTaskIds || []),
      ...(result.cleanedTaskIds || []),
    ]);
    set((state) => ({
      results: state.results
        .filter((task) => !removedTaskIds.has(task.id))
        .map((task) => ({
          ...task,
          outputs: task.outputs.filter((output) => !idSet.has(output.id)),
        }))
        .filter((task) => task.outputs.length > 0),
    }));
    return normalizeDeleteResult(result);
  },

  clearAllResults: async () => {
    const epoch = captureResultEpoch();
    const result = await apiRequest<ClearResultsResult>('/api/results/all', { method: 'DELETE' });
    if (!isResultEpochCurrent(epoch)) return result;
    const filters = get().filters;
    get().reset();
    set({ filters });
    await get().loadResults();
    return result;
  },

  exportResult: async (taskId, outputIds, exportDir) => {
    const result = await window.electron.invoke(window.IPC_CHANNELS.RESULT_EXPORT, {
      taskId,
      outputIds,
      exportDir,
    });
    return result.exported || [];
  },

  reset: () => {
    resultsCursor = null;
    resultsLoadedCount = 6;
    activeResultMore = undefined;
    lastResultLoad = undefined;
    activeResultLoad = undefined;
    ++resultEpoch;
    ++resultLoadSequence;
    set({ results: [], isLoading: false, isLoadingMore: false, hasMore: false,filters:{},loadError:null });
  },
}));

function normalizeDeleteResult(result: Partial<DeleteOutputsResult>): DeleteOutputsResult {
  return {
    deleted: result.deleted || 0,
    mode: result.mode === 'cleaned' ? 'cleaned' : 'hidden',
    hiddenTaskIds: result.hiddenTaskIds || [],
    cleanedTaskIds: result.cleanedTaskIds || [],
  };
}
